import {
  EAppSupportType,
  IComponent,
  IComponentChild,
  IComponentDto,
  IComponentSetDto,
  IProperty,
  IQuickProp,
  IQuickPropIssue,
  isForwardCompatQuickPropIssue,
  validateQuickProps
} from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { AxiosResponse } from "axios";
import jsonStringifySafe from "json-stringify-safe";
import * as React from "react";
import { ComponentService } from "../../../../services/component-service";
import { ComponentSetService } from "../../../../services/component-set-service";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { IWithFormProp, WithForm } from "../../../../shared/hoc/with-form";
import { IWithHistoryProp, withHistory } from "../../../../shared/hoc/with-history";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../shared/utility/save-status";
import KuikaButton, { type KuikaButtonStatus } from "../../../ui-kit/kuika-button/kuika-button";
import { KuikaSkeletonForm, KuikaSkeletonRegion } from "../../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaStepper, { KuikaStepperStep } from "../../../ui-kit/kuika-stepper/kuika-stepper";
import { toast } from "../../../ui-kit/kuika-toast";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import KuikaTitle from "../../../ui-kit/typography/Title/Title";
import "./component-detail.scss";
import { ComponentFormFifthStep } from "./component-def-fifth-step";
import { ComponentFormFirstStep } from "./component-def-firs-step";
import { ComponentFormQuickPropsStep } from "./component-def-quick-props-step";
import { ComponentFormSecondStep } from "./component-def-second-step";

/**
 * The backend's `ComponentModel` wrapper carries `Purpose`/`DesignGuidance` (PascalCase wire keys —
 * see `Backend/CoreApi.Data/Models/ComponentModel.cs`), but `IComponentDto` in kuika-cl-model does not
 * declare them. Widening the shared DTO is out of scope here (kuika-cl-model is a Top-tier compatibility
 * surface); a local extension lets a save round-trip these fields instead of the backend's Update path —
 * which overwrites the whole stored record from the request body — silently blanking them.
 */
export interface IComponentWrapperDto extends IComponentDto {
  Purpose?: string;
  DesignGuidance?: string;
}

export interface IComponentDetailState {
  componentDTO: IComponentDto;
  step: number;
  numberOfSteps: number;
  component: IComponent;
  properties: IProperty[];
  blockStyle: number;
  componentType: number;
  selectedComponentSetId: string;
  /** Where the save stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  isClicked: boolean;
  isThirdStepSelected: boolean;
  isFourStepSelected: boolean;
  selectedChildren: IComponentChild[];
  isLoading: boolean;
  inVisibleStyles: string[];
  quickProps: IQuickProp[];
}
export interface IComponentDetailProps {
  turnBack: any;
  componentId: IComponentDto;
  selectedComponentSet: IComponentSetDto;
}
class ComponentDetail extends React.PureComponent<
  IComponentDetailProps & IWithContextProp & IWithHistoryProp & IWithFormProp<IComponentDetailProps>,
  IComponentDetailState
> {
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(
    props: IComponentDetailProps & IWithContextProp & IWithHistoryProp & IWithFormProp<IComponentDetailProps>
  ) {
    super(props);
    const componentDTO = {} as IComponentDto;
    const component = {} as IComponent;
    component.name = "";
    this.state = {
      componentDTO,
      step: 0,
      numberOfSteps: 3,
      component: {} as IComponent,
      properties: [],
      blockStyle: -1,
      componentType: -1,
      selectedComponentSetId: "",
      saveStatus: "idle",
      isClicked: false,
      isThirdStepSelected: false,
      isFourStepSelected: false,
      selectedChildren: [],
      isLoading: false,
      inVisibleStyles: [],
      quickProps: []
    };
  }

  componentWillUnmount(): void {
    this.saveStatus.dispose();
  }

  async componentDidMount() {
    const id = this.props.componentId;
    if (id) {
      this.loadComponent(id);
    }
  }

  loadComponent = async (id: IComponentDto) => {
    this.setState({ isLoading: true });
    if (!id.id) {
      this.setState({
        component: {} as IComponent,
        selectedChildren: [],
        isLoading: false
      });
      return;
    }
    // get this component
    const res = this.props.componentId;
    const json = JSON.parse(this.props.componentId.json);
    json.id = res.id;
    json.componentType = Number(json.componentType);
    json.blockStyle = Number(json.blockStyle);
    json.properties.forEach((p) => {
      if (p.options && p.options.length > 0) {
        p.hasOptions = true;
      } else {
        p.hasOptions = false;
      }
    });
    const childrenNames: string[] = [];
    if (json.childrenInfo && json.childrenInfo.length > 0) {
      json.childrenInfo.forEach((child: IComponentChild) => {
        childrenNames.push(child.name);
        json[`set${child.name}`] = child.count;
        json[`fix${child.name}`] = child.fixed;
      });
      json.childrenNames = childrenNames as any;
    } else if (json.childrenNames) {
      json.childrenInfo = [];
      json.childrenNames.forEach((childName: string) => {
        childrenNames.push(childName);
        json[`set${childName}`] = 1;
        json[`fix${childName}`] = false;
        json.childrenInfo?.push({ name: childName, count: 0 });
      });
    }
    const resData = await ComponentSetService.Get(res.kComponentSet_Id);
    let componentCategory;
    if (resData.status === 200) {
      const { data } = resData;
      const parsedJSON = JSON.parse(data.json);
      const categoryList = parsedJSON?.categoryList;
      componentCategory = categoryList?.find((x) => x.categoryName === json.componentCategoryName)?.categoryName;
    }
    if (!componentCategory) {
      componentCategory = json.componentCategoryName;
      json.componentCategoryName = componentCategory;
      json.componentCategoryIcon = componentCategory;
    }
    this.setState({
      component: json,
      isLoading: false,
      selectedChildren: json.childrenInfo === undefined ? [] : json.childrenInfo,
      inVisibleStyles: json.inVisibleStyles && json.inVisibleStyles.length > 0 ? json.inVisibleStyles : [],
      quickProps: json.quickProps && json.quickProps.length > 0 ? json.quickProps : []
    });
    json.componentCategory = componentCategory;

    json.defaultStylingTypography = this.defaultStyleIdsStringToArray(json.defaultStylingTypography);
    json.defaultStylingPosition = this.defaultStyleIdsStringToArray(json.defaultStylingPosition);
    json.defaultStylingBorder = this.defaultStyleIdsStringToArray(json.defaultStylingBorder);
    json.defaultStylingShadow = this.defaultStyleIdsStringToArray(json.defaultStylingShadow);
    json.defaultStylingFill = this.defaultStyleIdsStringToArray(json.defaultStylingFill);

    this.props.form.setFieldsValue(json);
  };

  refactorProperties = () => {
    const updatedProperties = this.state.properties;
    updatedProperties.forEach((p) => {
      if (p.selectedType) {
        if (p.type.indexOf(p.selectedType) === -1) {
          p.selectedType = p.type[0];
        }
        if (p && p.typeOptions && KMainFunctions.isObjectEmpty(p.typeOptions) === false && p.typeOptions.find) {
          const pType = p.typeOptions.find((x) => Number(x.propertyType) === p.selectedType);
          if (pType) {
            p.hasOptions = pType.hasOptions;
            p.options = pType.options;
          }
        }
      }
    });
    this.setState({ properties: updatedProperties });
    return updatedProperties;
  };

  defaultStyleIdsStringToArray(defaultStyleIdsString: any) {
    if (!defaultStyleIdsString) return;

    return defaultStyleIdsString.toUpperCase().split("|");
  }

  defaultStyleIdsToString(defaultStyleIDs: any) {
    if (!defaultStyleIDs || typeof defaultStyleIDs === "string") return defaultStyleIDs;

    let result = "";

    defaultStyleIDs.forEach((id, idx) => {
      result += id;
      if (idx !== defaultStyleIDs.length - 1) result += "|";
    });

    return result;
  }

  onSubmit = async (data: any) => {
    try {
      const isValid = await this.validateFormSteps(this.state.step);
      if (!isValid) return;
      // Save is reachable from every step (the footer's Save button isn't step-gated), so this can't
      // live only in validateFormSteps's per-step dispatch — that only ever checks the step the user
      // happens to be on. Checked unconditionally so a quickProps entry left dangling by an edit on an
      // earlier step (a renamed/deleted property, a removed child) can never reach the saved JSON.
      //
      // Only BLOCKING issues gate the save. An entry this build doesn't understand (a `kind` or style
      // key added by a newer designer) is warned about and saved back verbatim — forcing its deletion
      // would let an older designer silently strip curation a newer one wrote.
      const quickPropIssues: IQuickPropIssue[] = this.getQuickPropIssues();
      const blockingIssue: IQuickPropIssue | undefined = quickPropIssues.find(
        (issue: IQuickPropIssue): boolean => !isForwardCompatQuickPropIssue(issue.code)
      );
      if (blockingIssue !== undefined) {
        toast.warning(blockingIssue.message);
        this.setState({ step: 3 });
        return;
      }
      if (quickPropIssues.length > 0) {
        toast.warning("Some quick props come from a newer designer version. They will be kept unchanged.");
      }
      // A second Enter while the save runs or its ✓ is held would save twice and cancel the return.
      if (!this.saveStatus.canStart) return;
      this.saveStatus.start();
      if (!data.isItChildItem) {
        data.isItChildItem = false;
      }
      if (!data.hasValidate) {
        data.hasValidate = false;
      }
      if (!data.isItNavigationComponent) {
        data.isItNavigationComponent = false;
      }
      if (!data.isItHiddenFromComponentList) {
        data.isItHiddenFromComponentList = false;
      }
      if (!data.appSupportType) {
        data.appSupportType = EAppSupportType.All;
      }
      if (!data.isDeprecated) {
        data.isDeprecated = false;
      }
      if (!data.dragDropAreaText) {
        data.dragDropAreaText = "";
      }
      if (!data.isRenamingDisabled) {
        data.isRenamingDisabled = false;
      }
      if (!data.wrapWithFormItem) {
        data.wrapWithFormItem = false;
      }
      if (!data.isReportDesignComponent) {
        data.isReportDesignComponent = false;
      }
      if (!data.isDataRepeater) {
        data.isDataRepeater = false;
      }
      // Start from the full loaded wrapper (Purpose/DesignGuidance/createdAt/... included at
      // runtime even though IComponentDto doesn't declare all of them) rather than a bare `{}`.
      // The backend's Update path overwrites the whole stored record from this request body, so a
      // payload that only carried name/kComponentSet_Id/json used to blank every other field.
      const compObj: IComponentWrapperDto = { ...(this.props.componentId as IComponentWrapperDto) };
      const formInputs: any = {};
      compObj.name = KMainFunctions.capitalizeFirstLetter(data.name);
      compObj.kComponentSet_Id = this.props.selectedComponentSet.id;
      formInputs.id = this.state.component.id;
      formInputs.name = KMainFunctions.capitalizeFirstLetter(data.name);
      formInputs.isItChildItem = data.isItChildItem;
      formInputs.hasValidate = data.hasValidate;
      formInputs.isItNavigationComponent = data.isItNavigationComponent;
      formInputs.isItHiddenFromComponentList = data.isItHiddenFromComponentList;
      formInputs.appSupportType = data.appSupportType;
      formInputs.isDeprecated = data.isDeprecated;
      formInputs.isRenamingDisabled = data.isRenamingDisabled;
      formInputs.wrapWithFormItem = data.wrapWithFormItem;
      formInputs.titleAttributeName = data.titleAttributeName;
      formInputs.tagName = data.tagName;
      formInputs.defaultStylingTypography = this.defaultStyleIdsToString(data.defaultStylingTypography);
      formInputs.defaultStylingPosition = this.defaultStyleIdsToString(data.defaultStylingPosition);
      formInputs.defaultStylingBorder = this.defaultStyleIdsToString(data.defaultStylingBorder);
      formInputs.defaultStylingShadow = this.defaultStyleIdsToString(data.defaultStylingShadow);
      formInputs.defaultStylingFill = this.defaultStyleIdsToString(data.defaultStylingFill);
      formInputs.renderer = data.renderer;
      formInputs.icon = data.icon;
      formInputs.componentCategory = data.componentCategory;
      formInputs.blockStyle = data.blockStyle;
      formInputs.componentType = data.componentType;
      formInputs.isReportDesignComponent = data.isReportDesignComponent;

      if (data.componentCategory !== undefined) {
        formInputs.componentCategoryIcon = data.componentCategory;
        formInputs.componentCategoryName = data.componentCategory;
      }

      formInputs.componentSetId = this.props.selectedComponentSet.id;
      formInputs.childrenInfo = [];
      formInputs.childrenNames = [];
      formInputs.dragDropAreaText = data.dragDropAreaText;
      formInputs.isDataRepeater = data.isDataRepeater;
      data.childrenNames?.forEach((childName) => {
        const defaultCount = data[`set${childName}`];
        const fixed = data[`fix${childName}`];
        formInputs.childrenNames.push(childName);
        if (this.state.selectedChildren) {
          const child = this.state.selectedChildren.find((c) => c.name === childName);
          if (child) {
            (formInputs.childrenInfo as IComponentChild[]).push({
              name: childName,
              count: defaultCount || child.count,
              fixed
            } as IComponentChild);
          }
        }
      });
      formInputs.properties = this.refactorProperties();
      formInputs.children = [];
      formInputs.inVisibleStyles = this.state.inVisibleStyles;
      formInputs.quickProps = this.state.quickProps;
      delete formInputs.selectedChildId;
      const strJson = jsonStringifySafe(formInputs);
      compObj.json = strJson;
      const isUpdate: boolean = Boolean(this.state.component && this.state.component.id);
      const res: AxiosResponse = isUpdate
        ? await ComponentService.Update(this.state.component.id, compObj)
        : await ComponentService.Create(compObj);
      if (res && res.status === 200) {
        // The ✓ goes up first and the form returns to the card list after it, where the list
        // reloads with the saved component — no toast was ever said here.
        this.saveStatus.succeed((): void => this.props.turnBack?.());
      } else {
        // Before, a refused save left the button spinning for good; the form stays open either way.
        this.saveStatus.fail();
      }
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      this.saveStatus.fail();
    }
  };

  onCheck = (checkedKeysValue: any, info: any) => {
    this.setState({ inVisibleStyles: checkedKeysValue });
  };

  onQuickPropsChange = (quickProps: IQuickProp[]): void => {
    this.setState({ quickProps });
  };

  validateFormItems = async (itemNames: string[]) => {
    let returnedValue = false;
    await this.props.form
      .validateFields(itemNames)
      .then((values) => {
        if (values.selectedComponentSet?.id) {
          this.setState({ selectedComponentSetId: values.selectedComponentSet?.id });
        }
        returnedValue = true;
      })
      .catch((err) => {
        if (err.errorFields.length > 0) {
          returnedValue = false;
        } else {
          returnedValue = true;
        }
      });
    return returnedValue;
  };

  async validateFormSteps(stepNumber) {
    let returnedValue = false;
    if (stepNumber === 0) {
      returnedValue = await this.validateFormItems(["name", "componentSetId", "blockStyle", "componentType"]);
    } else if (stepNumber === 1) {
      let isValidated = true;
      this.state.component.properties?.forEach((p, idx) => {
        if (p.isRequired === true) {
          if (p.symbol.fixedValue === "") {
            toast.warning(`Please add a value for "${p.name}" property. Because this property is required.`);
            isValidated = false;
          }
        }
      });
      returnedValue = isValidated;
    } else if (stepNumber === 2) {
      returnedValue = true;
    } else {
      // Deliberately not gated here for stepNumber === 3 (Quick Props): validateFormSteps also runs
      // when LEAVING a step (previousStep/goToSpecificStep validate the step being left), and a
      // quickProps issue is fixed by navigating BACK to Properties/Child Component — gating this step
      // the same way step 1 gates itself would trap the user on it. Save is still blocked
      // unconditionally by `getQuickPropIssues()` in `onSubmit`, regardless of which step is current.
      return (returnedValue = true);
    }
    return returnedValue;
  }

  /**
   * `state.properties` only reflects what `ComponentFormSecondStep` has actively edited — it starts
   * `[]` and is filled in by that step's OWN `componentDidUpdate`, which (being a lifecycle hook) never
   * fires on its first mount. Right after a load, before the user has touched a property, it is empty
   * even though the component plainly has properties — `state.component.properties` (populated
   * synchronously by `loadComponent`, in the same `setState` that flips `isLoading` off) is the correct
   * read in that window. Same fallback rule `ComponentFormSecondStep.componentDidUpdate` itself already
   * uses to decide whether to hydrate `state.properties` in the first place.
   */
  getLiveProperties(): IProperty[] {
    return this.state.properties.length > 0 ? this.state.properties : (this.state.component.properties ?? []);
  }

  /**
   * `state.selectedChildren` is NOT the save-time source of truth for children: it is seeded from the
   * loaded component and `getInputChangeFromFifthStep` only ever MERGES into it (a per-child "default
   * count" edit adds/updates an entry) — it never drops one. The child-selection `KuikaSelectbox`
   * itself (`component-def-fifth-step.tsx`) is a plain `Form.Item name="childrenNames"`, and `onSubmit`
   * saves `formInputs.childrenNames` straight from that field's submitted value — so removing a child
   * there would save a shorter list while this kept reporting the removed one as still present. Reading
   * the live form field instead keeps quickProps validation looking at exactly what Save is about to
   * persist.
   */
  getLiveChildrenNames(): string[] {
    const fieldValue: unknown = this.props.form.getFieldValue("childrenNames");
    return Array.isArray(fieldValue) ? (fieldValue as string[]) : [];
  }

  getQuickPropIssues(): IQuickPropIssue[] {
    return validateQuickProps({
      name: this.state.component?.name ?? "",
      properties: this.getLiveProperties(),
      inVisibleStyles: this.state.inVisibleStyles,
      childrenNames: this.getLiveChildrenNames(),
      quickProps: this.state.quickProps
    });
  }

  getPropertiesInfo = (properties: IProperty[]) => {
    this.setState({ properties });
  };

  getBlockStyle = (number) => {
    this.setState({ blockStyle: number });
  };

  getComponentType = (number) => {
    this.setState({ componentType: number });
  };

  async nextStep() {
    let nextStep = this.state.step;
    const isValid = await this.validateFormSteps(nextStep);
    if (nextStep < this.state.numberOfSteps && isValid === true) {
      nextStep++;
      this.setState({ step: nextStep });
    }
  }

  async previousStep() {
    let previousStep = this.state.step;
    const isValid = await this.validateFormSteps(previousStep);
    if (previousStep > 0 && isValid === true) {
      previousStep--;
      this.setState({ step: previousStep });
    }
  }

  goToSpecificStep = async (stepNumber) => {
    const isValid = await this.validateFormSteps(this.state.step);
    if (isValid) {
      this.setState({ step: stepNumber });
    }
  };

  handleThirdStepSelected = (val: boolean) => {
    this.setState({ isThirdStepSelected: val });
  };

  handleFourStepSelected = (val: boolean) => {
    this.setState({ isFourStepSelected: val });
  };

  getInputChangeFromFifthStep = (newCount, newName) => {
    const array = [...this.state.selectedChildren];
    array.push({ name: newName, count: newCount, fixed: false });
    const unique = [...(new Map(array.map((item) => [item.name, item])).values() as any)];
    this.setState({ selectedChildren: unique });
  };

  handleClickCancel = () => {
    this.props.turnBack();
    this.setState({
      component: {} as IComponent,
      selectedChildren: []
    });
  };

  getSteps = (): KuikaStepperStep[] => {
    return [
      { key: "mainInformation", title: "Main Information", description: "Identity, rendering and defaults" },
      { key: "properties", title: "Properties", description: "The attributes designers bind" },
      { key: "childComponent", title: "Child Component", description: "What this component may contain" },
      { key: "quickProps", title: "Quick Props", description: "Shortcuts on the canvas selection toolbar" }
    ];
  };

  render() {
    const isEditing: boolean = Boolean(this.state?.component?.id);
    const componentName: string = this.state?.component?.name ?? "";
    const canSave: boolean =
      this.props.selectedComponentSet.isPublic === false ||
      KMainFunctions.isUserKuikaDeveloper(this.props.context?.userInfo?.username);
    const isLastStep: boolean = this.state.step >= this.state.numberOfSteps;

    return (
      <div className="kuika-component-detail" data-testid="componentDetail">
        {/* The form's shape while the component loads, instead of darkening the whole page (L7). */}
        {this.state.isLoading && (
          <KuikaSkeletonRegion label="Loading component">
            <KuikaSkeletonForm fields={5} />
          </KuikaSkeletonRegion>
        )}
        {/* Mount only once the component is loaded. `loadComponent` fills the form
            store immediately after clearing `isLoading`, so fields that mount now
            read a value straight away instead of flipping from uncontrolled to
            controlled on the next tick. */}
        {!this.state.isLoading && (
          <div className="kuika-component-detail__panel" id="Kuika_componentForm">
            <header className="kuika-component-detail__header">
              <KuikaTitle data-testid="componentDetailTitle">
                {isEditing ? `Edit ${componentName}` : "New Component"}
              </KuikaTitle>
              <KuikaSmallText className="kuika-component-detail__subtitle">
                {isEditing
                  ? `Change how ${componentName} behaves for everyone designing with this component set.`
                  : "Define a component so your team can use it when designing pages."}
              </KuikaSmallText>
            </header>

            <KuikaStepper
              dataTestId="componentDetailStepper"
              className="kuika-component-detail__steps"
              steps={this.getSteps()}
              current={this.state.step}
              onChange={(index: number) => this.goToSpecificStep(index)}
            />

            <Form
              layout="vertical"
              form={this.props.form}
              name="basic"
              onFinish={this.onSubmit}
              className="kuika-component-detail__form"
            >
              {/* Every step stays mounted and is toggled with `hidden`: the submit
                whitelist reads all three steps' fields off one form store, so a
                step that unmounts takes its values with it. */}
              <div className="kuika-component-detail__body">
                <div hidden={this.state.step !== 0}>
                  <ComponentFormFirstStep
                    component={this.state.component}
                    selectedComponentSetId={this.props.selectedComponentSet.id}
                    getBlockStyle={this.getBlockStyle}
                    getComponentType={this.getComponentType}
                    inVisibleStyles={this.state.inVisibleStyles}
                    onCheck={this.onCheck}
                  />
                </div>
                <div hidden={this.state.step !== 1}>
                  <ComponentFormSecondStep
                    component={this.state.component}
                    properties={this.state.properties}
                    getPropertiesInfo={this.getPropertiesInfo}
                  />
                </div>
                <div hidden={this.state.step !== 2}>
                  <ComponentFormFifthStep
                    selectedChildren={this.state.selectedChildren}
                    component={this.state.component}
                    componentSetId={this.props.selectedComponentSet.id}
                    getInputChangeFromFifthStep={this.getInputChangeFromFifthStep.bind(this)}
                  />
                </div>
                <div hidden={this.state.step !== 3}>
                  <ComponentFormQuickPropsStep
                    component={this.state.component}
                    properties={this.getLiveProperties()}
                    inVisibleStyles={this.state.inVisibleStyles}
                    childrenNames={this.getLiveChildrenNames()}
                    quickProps={this.state.quickProps}
                    onChange={this.onQuickPropsChange}
                  />
                </div>
              </div>

              <footer className="kuika-component-detail__footer">
                <div className="kuika-component-detail__footer-left">
                  {this.state.step > 0 && (
                    <KuikaButton
                      data-testid="componentDetailPreviousButton"
                      type="secondary"
                      onClick={() => this.previousStep()}
                    >
                      Previous
                    </KuikaButton>
                  )}
                </div>
                <div className="kuika-component-detail__footer-right">
                  {canSave && (
                    <KuikaButton
                      data-testid="componentDetailSaveButton"
                      type={isLastStep ? "primary" : "secondary"}
                      htmlType="submit"
                      status={this.state.saveStatus}
                    >
                      Save
                    </KuikaButton>
                  )}
                  {!isLastStep && (
                    <KuikaButton data-testid="componentDetailNextButton" type="primary" onClick={() => this.nextStep()}>
                      Next
                    </KuikaButton>
                  )}
                </div>
              </footer>
            </Form>
          </div>
        )}
      </div>
    );
  }
}

const componentDetail = WithForm<IComponentDetailProps>(
  withContext<IComponentDetailProps>(withHistory<IComponentDetailProps>(ComponentDetail))
);
export { componentDetail as ComponentDetail };
